<template>
  <InstructorLayout>
    <div class="instructor-home">
      <!-- ヘッダーエリア（モチベーション管理） -->
      <div class="motivation-header">
        <div class="motivation-stats">
          <div class="stat-item">
            <span class="stat-icon">📢❤️</span>
            <span class="stat-label">本日の応援人数</span>
            <span class="stat-value">{{ todayFeedbackCount }}人</span>
          </div>
        </div>
      </div>

      <!-- インフォメーションエリア -->
      <div class="info-section">
        <div v-if="waitingCount > 0" class="info-message">
          現在 <strong>{{ waitingCount }}</strong> 人の生徒があなたの声を待っています！
        </div>
        <div v-else class="info-message success">
          🎉 現在、待機中の生徒はいません。お疲れ様でした！
        </div>
      </div>

      <!-- Day0ミニ体験への導線 -->
      <router-link to="/instructor/day0-submissions" class="day0-link-card">
        <span class="day0-link-icon">🎤</span>
        <span class="day0-link-text">
          <span class="day0-link-title">Day0ミニ体験の提出</span>
          <span class="day0-link-sub">本登録前の体験にフィードバックを送る</span>
        </span>
        <span class="day0-link-arrow">›</span>
      </router-link>

      <!-- タスクカードエリア -->
      <div class="tasks-section">
        <div v-if="isLoading" class="loading">
          <p>データを読み込み中...</p>
        </div>

        <div v-else-if="tasks.length === 0" class="no-tasks">
          <p>現在、対応可能なタスクはありません</p>
        </div>

        <div v-else class="task-cards">
          <div
            v-for="task in tasks"
            :key="task.id"
            class="task-card"
            :class="{ 'waiting-long': task.waitingHours >= 12 }"
          >
            <!-- 生徒情報 -->
            <div class="student-info">
              <div class="student-name">
                {{ task.student_name }} <span class="student-grade">({{ task.grade }})</span>
              </div>
              <div class="assignment-info">
                <span class="day-label">Day {{ task.day }}</span>
                <span class="assignment-title">（{{ task.assignment_title }}）</span>
                <span v-if="task.submissionCount > 1" class="submission-count-badge">{{ task.submissionCount }}回提出</span>
              </div>
              <div class="metrics">
                <div class="metric-item">
                  <span class="metric-icon">🕒</span>
                  <span class="metric-label">待機:</span>
                  <span class="metric-value" :class="{ 'waiting-long': task.waitingHours >= 12 }">
                    {{ formatWaitingTime(task.waitingHours) }}
                  </span>
                </div>
                <div class="metric-item">
                  <span class="metric-icon">⏱️</span>
                  <span class="metric-label">練習:</span>
                  <span class="metric-value">{{ formatDuration(task.practice_duration) }}</span>
                </div>
                <div class="metric-item">
                  <span class="metric-icon">🎵</span>
                  <span class="metric-label">音声:</span>
                  <span class="metric-value">{{ formatDuration(task.audio_duration) }}</span>
                </div>
              </div>
            </div>

            <!-- 発注条件ブロック -->
            <div class="order-terms">
              <div class="terms-row terms-row-fixed">
                <span class="term-item">
                  <span class="term-label">[業務]</span>
                  <span class="term-value">音声FB作成</span>
                </span>
                <span class="term-item">
                  <span class="term-label">[報酬]</span>
                  <span class="term-value">150円(税込)</span>
                </span>
              </div>
              <div class="terms-row terms-row-fixed">
                <span class="term-item">
                  <span class="term-label">[納期]</span>
                  <span class="term-value">受注後15分</span>
                </span>
                <span class="term-item">
                  <span class="term-label">[支払]</span>
                  <span class="term-value">翌月10日払い</span>
                </span>
              </div>
              <div class="terms-agreement">
                ※ボタン押下をもって、上記条件および利用規約に同意し業務を受託します。
              </div>
            </div>

            <!-- アクションボタン -->
            <div class="task-actions">
              <button
                @click="handleAcceptTask(task)"
                class="btn btn-accept"
                :disabled="isProcessing"
              >
                🔥 この子に応える！
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- リスト操作エリア -->
      <div class="list-actions">
        <button
          @click="refreshTaskList"
          class="btn btn-refresh"
          :disabled="isLoading || isProcessing"
        >
          🔄 リストを最新に更新
        </button>
      </div>

      <!-- 競合時のメッセージ表示 -->
      <div v-if="conflictMessage" class="conflict-message">
        <p>{{ conflictMessage }}</p>
      </div>
      
      <!-- グローバルナビゲーション -->
      <InstructorNavigation />
    </div>

    <!-- 全画面フィードバックモーダル -->
    <Transition name="fullscreen-modal">
      <div v-if="showFullScreenFeedback" class="fullscreen-feedback-modal" @click.self="canCloseModal ? closeFullScreenFeedback() : null">
        <div class="fullscreen-content">
          <!-- 閉じるボタン -->
          <button @click="closeFullScreenFeedback" class="close-btn" :disabled="!canCloseModal" :aria-disabled="!canCloseModal" :tabindex="!canCloseModal ? -1 : 0" :title="canCloseModal ? '閉じる' : '操作中のため閉じられません'">
            {{ canCloseModal ? '✕' : '⏳' }}
          </button>
          
          <!-- ロック残り時間（提案1: タイマー表示 + バックグラウンド復帰時の自動判定） -->
          <div class="lock-timer-bar" :class="lockTimerClass">
            <span class="lock-timer-label">⏱️ 残り時間:</span>
            <span class="lock-timer-value">{{ remainingTimeDisplay }}</span>
            <span v-if="showExpiryWarning" class="lock-timer-warning">まもなく期限切れです</span>
          </div>
          
          <!-- フィードバック画面の内容 -->
          <div class="feedback-screen">
            <div class="feedback-header-mobile">
              <div class="student-name-line">{{ currentFullScreenSubmission?.student_name || '未設定' }}</div>
              <div v-if="currentFullScreenSubmission?.student_name_kana" class="student-kana-line">{{ currentFullScreenSubmission.student_name_kana }}</div>
              <div class="assignment-line">Day {{ currentFullScreenSubmission?.day }} {{ currentFullScreenSubmission?.type === 'practice' ? '練習' : '課題' }}</div>
            </div>
            
            <!-- フィードバック機能セクション -->
            <div class="feedback-sections">
              
              <!-- 1. フィードバックマニュアル -->
              <div class="feedback-section feedback-manual-section">
                <h3 class="manual-title">📖 フィードバックの手順</h3>
                <div class="manual-content">
                  <div class="manual-steps">
                    <p>1.（下の名前）さんへ、<br>Day△△お疲れさまでした！</p>
                    <p>2.今日の良かったところは・・・。<br>（最初に良かった点を1つ以上伝える）</p>
                    <p>3.今日の復習は、〇〇の部分です。<br>△△を意識して3回、読んでみましょう！</p>
                    <p>4.（あれば一言加えて、）明日の提出も楽しみにしています！</p>
                  </div>
                </div>
              </div>

              <!-- 2. Day課題内容表示 -->
              <div class="feedback-section feedback-assignment-section">
                <h3>📋 Day {{ currentFullScreenSubmission?.day }} 課題内容</h3>
                <div class="assignment-content">
                  <p><strong>課題タイトル:</strong> Day {{ currentFullScreenSubmission?.day }} の発音練習</p>
                  <p><strong>練習内容:</strong> 指定された英単語・フレーズの発音練習</p>
                  <p><strong>評価ポイント:</strong> 発音の正確性、イントネーション、流暢性</p>
                </div>
              </div>

              <!-- 音声・録音セクション（複数提出の再生 + 固定表示用） -->
              <div class="feedback-controls-section">
                <!-- 提出音源再生（複数本） -->
                <div class="feedback-section feedback-audio-section">
                  <h4 class="submitted-audio-title">提出音声（{{ currentFullScreenSubmissions?.length || 0 }}本）</h4>
                  <div class="submitted-audio-section">
                    <div
                      v-for="(sub, index) in currentFullScreenSubmissions"
                      :key="sub.id"
                      class="audio-slot"
                    >
                      <div class="audio-slot-label">提出{{ index + 1 }}</div>
                      <button
                        v-if="!players[sub.id]"
                        class="btn btn-primary"
                        @click="prefetchPlaybackUrl(sub.id)"
                        :disabled="loadingId === sub.id"
                      >
                        {{ loadingId === sub.id ? '準備中…' : '🔗 URLを取得' }}
                      </button>
                      <div v-else class="audio-player-section">
                        <audio
                          :ref="setAudioRef(sub.id)"
                          :src="players[sub.id]?.url"
                          controls
                          preload="metadata"
                          playsinline
                          class="fullscreen-audio-player"
                          controlslist="nodownload noplaybackrate"
                          @play="ensureTimer(sub.id)"
                          @stalled="refreshUrl(sub.id, true)"
                          @error="refreshUrl(sub.id, true)"
                          @seeking="maybeSeekRefresh(sub.id)"
                          @ended="onPlaybackEnded(sub.id)"
                        ></audio>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- フィードバック録音機能 -->
                <div class="feedback-section feedback-recording-section">
                  <div class="feedback-recording-section">
                    
                    <div v-if="canShowStartButton && !isPreviewReady" class="recording-start">
                      <div class="media-kind-toggle">
                        <label>
                          <input type="radio" value="audio" v-model="selectedMediaKind" />
                          🎙️ 音声
                        </label>
                        <label :class="{ 'is-disabled': !isVideoSupported }">
                          <input type="radio" value="video" v-model="selectedMediaKind" :disabled="!isVideoSupported" />
                          🎬 動画
                        </label>
                        <p v-if="!isVideoSupported" class="media-kind-note">この端末では動画フィードバックに対応していません（Android/PCのみ対応）</p>
                      </div>
                      <button
                        v-if="selectedMediaKind === 'video'"
                        @click="onPrepareVideoPreview"
                        class="btn btn-primary large"
                        :disabled="!canStartNow || isPreparingPreview"
                      >
                        {{ isPreparingPreview ? '📷 カメラ準備中...' : '🎬 カメラ準備' }}
                      </button>
                      <button v-else @click="startRecording" class="btn btn-primary large" :disabled="!canStartNow">
                        🎙️ 録音開始
                      </button>
                  </div>

                  <!-- 動画: 録画開始前のプレビュー確認・向き補正フェーズ -->
                  <div v-if="isPreviewReady && !isRecording" class="video-preview-section">
                    <div class="preview-outer">
                      <video
                        :ref="setPreviewVideoEl"
                        class="recording-preview"
                        :style="previewVideoStyle"
                        autoplay
                        muted
                        playsinline
                      ></video>
                    </div>
                    <button @click="onRotatePreview" class="btn btn-secondary rotate-btn">↻ 向きを直す</button>
                    <div class="preview-actions">
                      <button @click="onStartVideoRecording" class="btn btn-primary large">🎬 録画開始</button>
                      <button @click="onCancelVideoPreview" class="btn btn-secondary">キャンセル</button>
                    </div>
                  </div>

                  <div v-if="isRecording" class="recording-active">
                    <div v-if="selectedMediaKind === 'video'" class="preview-outer">
                      <video
                        :ref="setPreviewVideoEl"
                        class="recording-preview"
                        :style="previewVideoStyle"
                        autoplay
                        muted
                        playsinline
                      ></video>
                    </div>
                    <div class="recording-indicator">
                      <span class="recording-dot"></span>
                      <span>録音中...</span>
                    </div>
                    <div class="recording-time">{{ formatRecordingTime(recordingTime) }}</div>
                    <button
                      @click="stopRecording"
                      class="btn btn-danger"
                      :disabled="!canStopRecording"
                      :class="{ 'btn-disabled': !canStopRecording }"
                    >
                      {{ canStopRecording ? '⏹️ 録音停止' : `⏹️ 録音停止 (${Math.max(0, 3 - recordingTime)}秒後)` }}
                    </button>
                  </div>

                  <!-- アップロード進捗表示 -->
                  <div v-if="isUploading" class="upload-progress-section">
                    <div class="upload-indicator">
                      <span class="upload-spinner">📤</span>
                      <span>アップロード中...</span>
                    </div>
                    <div class="progress-bar">
                      <div class="progress-fill" :style="{ width: uploadProgress.percentage + '%' }"></div>
                    </div>
                    <div class="progress-text">
                      {{ uploadProgress.completed }} / {{ uploadProgress.total }} チャンク
                    </div>
                  </div>

                  <!-- 録音完了後の音声プレイヤーと操作ボタン -->
                  <div v-if="isCompleted && !isRecording && !isUploading" class="recording-completed">
                    <div class="completed-message">
                      <span class="success-icon">✅</span>
                      <span>録音が完了しました</span>
                    </div>
                    
                    <!-- 音声/動画プレイヤー（自己確認用） -->
                    <div v-if="finalAudioUrl" class="audio-player-section">
                      <video
                        v-if="selectedMediaKind === 'video'"
                        :src="finalAudioUrl"
                        controls
                        playsinline
                        preload="metadata"
                        class="feedback-video-player"
                        controlslist="nodownload noplaybackrate"
                      >
                        お使いのブラウザは動画再生に対応していません。
                      </video>
                      <audio
                        v-else
                        :src="finalAudioUrl"
                        controls
                        preload="metadata"
                        class="feedback-audio-player"
                        controlslist="nodownload noplaybackrate"
                        playsinline
                      >
                        お使いのブラウザは音声再生に対応していません。
                      </audio>
                    </div>
                    
                    <div class="recording-actions">
                      <button 
                        @click="retakeRecording" 
                        class="btn btn-secondary"
                        :disabled="isDeleting"
                      >
                        {{ isDeleting ? '🗑️ 削除中...' : '🔄 撮り直し' }}
                      </button>
                      <button @click="handleSendFeedback" class="btn btn-success" :disabled="isDeleting">
                        📤 送信
                      </button>
                    </div>
                  </div>
                  
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </Transition>
  </InstructorLayout>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick, watch } from 'vue'
import { useRouter, onBeforeRouteLeave } from 'vue-router'
import { supabase } from '../../supabaseClient'
import InstructorLayout from '../../components/layout/InstructorLayout.vue'
import InstructorNavigation from '../../components/layout/InstructorNavigation.vue'
import { 
  players,
  loadingId,
  setAudioRef,
  refreshUrl,
  maybeSeekRefresh,
  stopPlayback,
  onPlaybackEnded,
  cleanupAllPlayers,
  prefetchPlaybackUrl,
  ensureTimer
} from '../../composables/useButtonTriggerJit.js'
import { useFeedbackRecorder } from '../../composables/useFeedbackRecorder.js'
import { isVideoFeedbackSupported } from '../../utils/videoRecorder.js'
import { formatDuration, formatWaitingTime, formatRemainingTime, acceptTaskWithLock, releaseFeedbackLock } from './instructorFeedbackHelpers.js'
import { getGradeFromBirthDate } from '../../utils/gradeFromBirthDate.js'

const router = useRouter()

// 状態管理
const isLoading = ref(false)
const isProcessing = ref(false)
const tasks = ref([])
const waitingCount = ref(0)
const todayFeedbackCount = ref(0)
const conflictMessage = ref('')

// 全画面フィードバック関連
const showFullScreenFeedback = ref(false)
const currentFullScreenSubmission = ref(null) // 代表（ヘッダー・送信用）
const currentFullScreenSubmissions = ref([]) // 複数提出の再生用
const isSubmitting = ref(false) // 二重提出防止

// ロック残り時間（提案1: タイマー表示 + バックグラウンド復帰時の自動判定）
const lockExpiresAt = ref(null) // 期限のタイムスタンプ（ms）
const lockTimerInterval = ref(null)
const remainingTimeDisplay = ref('--:--')
const remainingSeconds = ref(0)

// 新しいチャンク録音システム
const {
  isRecording,
  isUploading,
  isCompleted,
  canStopRecording,
  isDeleting,
  recordingTime,
  uploadProgress,
  currentRecording,
  finalAudioUrl,
  previewStream,
  isPreviewReady,
  previewRotationDeg,
  prepareVideoPreview,
  rotatePreview,
  cancelVideoPreview,
  startRecording: startFeedbackRecording,
  stopRecording: stopFeedbackRecording,
  cancelRecording: cancelFeedbackRecording,
  retakeRecording,
  sendToLine,
  formatTime: formatRecordingTime,
  resetState: resetFeedbackState,
  cleanup: cleanupRecorder,
  cleanupRecordingData,
  deleteFeedbackRecording
} = useFeedbackRecorder()

// 動画録画中のライブプレビュー（自撮り確認用。生カメラstreamをそのまま表示するだけ）
const previewVideoEl = ref(null)
function setPreviewVideoEl(el) {
  previewVideoEl.value = el
  // Vueはこの関数refを再描画のたびに呼び直すため、同じstreamへの再代入による
  // ちらつき（黒く点滅）を防ぐためすでに同じstreamなら何もしない
  if (el && el.srcObject !== (previewStream.value || null)) {
    el.srcObject = previewStream.value || null
  }
}
// 生ストリームは向きの補正をしていないため、プレビュー表示だけCSSで回転させる
// （実際に保存される映像の回転はサーバー側のffmpegが確定角度を使って行う）
const previewVideoStyle = computed(() => {
  const deg = previewRotationDeg.value
  const rotated = deg === 90 || deg === 270
  return {
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: rotated ? '133.333%' : '100%',
    height: rotated ? '75%' : '100%',
    objectFit: 'cover',
    transform: `translate(-50%, -50%) rotate(${deg}deg) scaleX(-1)`
  }
})
watch(previewStream, (stream) => {
  if (previewVideoEl.value && previewVideoEl.value.srcObject !== (stream || null)) {
    previewVideoEl.value.srcObject = stream || null
  }
})

const isPreparingPreview = ref(false)
// 「カメラ準備」ボタン: 録画開始前にカメラプレビューを準備する
async function onPrepareVideoPreview() {
  const submissionData = currentFullScreenSubmission.value
  if (!submissionData) return
  isPreparingPreview.value = true
  try {
    await prepareVideoPreview(submissionData)
  } catch {
    // エラー内容はprepareVideoPreview内でalert済み
  } finally {
    isPreparingPreview.value = false
  }
}
// 「向きを直す」ボタン: プレビューを90度ずつ回転（保存も内部で行う）
function onRotatePreview() {
  rotatePreview()
}
// プレビュー確認後の「録画開始」ボタン
async function onStartVideoRecording() {
  await startRecording()
}
// プレビューの「キャンセル」ボタン
function onCancelVideoPreview() {
  cancelVideoPreview()
}

// 認証トークン取得
async function getToken() {
  const { data: { session } } = await supabase.auth.getSession()
  return session?.access_token || ''
}

// 表示件数の切り替えフラグ。
// 過去の仕様：優先度（種類→締切）順に上位3件だけを「おすすめタスク」として自動表示していた。
// 現在の仕様：有料会員を優先したい等の判断は講師自身が行えるよう、待機中の提出を全件一覧表示し、
// 講師が自分で選んで返信できるようにする。将来的に上位3件表示に戻す可能性があるため、
// フラグをfalseに戻すだけで元の挙動に戻せるようにしてある。
const SHOW_ALL_SUBMISSIONS = true

// 待機中タスクを取得（1生徒1日=1タスク、返信済みは除外、代表のみロック）
// SHOW_ALL_SUBMISSIONS=true: 全件を一覧表示（講師が自分で選んで返信）
// SHOW_ALL_SUBMISSIONS=false: 従来通り、最も古い3グループのみを自動表示
const loadTasks = async () => {
  isLoading.value = true
  conflictMessage.value = ''
  
  try {
    await cleanupExpiredLocks()
    
    // 本日の応援人数を取得（日本時間の「今日」にフィードバック完了した件数・この講師分）
    const todayJst = new Date().toLocaleDateString('sv-SE', { timeZone: 'Asia/Tokyo' })
    const startOfDayJst = new Date(`${todayJst}T00:00:00+09:00`)
    const endOfDayJst = new Date(startOfDayJst)
    endOfDayJst.setDate(endOfDayJst.getDate() + 1)
    const startISO = startOfDayJst.toISOString()
    const endISO = endOfDayJst.toISOString()
    const { data: { user: currentUser } } = await supabase.auth.getUser()
    let todayQuery = supabase
      .from('submissions_v3')
      .select('id', { count: 'exact', head: true })
      .eq('type', 'assignment')
      .eq('status', 'feedback_completed')
      .gte('feedback_submitted_at', startISO)
      .lt('feedback_submitted_at', endISO)
    if (currentUser?.id) {
      todayQuery = todayQuery.eq('feedback_instructor_id', currentUser.id)
    }
    const { count: todayCount, error: todayError } = await todayQuery
    if (todayError) {
      console.warn('⚠️ 本日の応援人数取得エラー:', todayError)
    }
    todayFeedbackCount.value = todayCount ?? 0
    
    // 提出済みの課題を取得（グループ化用に多めに）。兄弟対応: learner_id を含める
    const { data: submissionData, error: submissionError } = await supabase
      .from('submissions_v3')
      .select('id, user_id, learner_id, day, type, duration, created_at')
      .eq('type', 'assignment')
      .eq('status', 'submitted')
      .order('created_at', { ascending: true })
      .limit(100)
    
    if (submissionError) throw submissionError
    
    // (user_id, learner_id, day) でグループ化（兄弟対応: 同一親・同日でも子ごとに別タスク）
    const groupMap = new Map()
    for (const s of submissionData || []) {
      const lid = s.learner_id ?? ''
      const key = `${s.user_id}-${lid}-${s.day}`
      if (!groupMap.has(key)) groupMap.set(key, [])
      groupMap.get(key).push(s)
    }
    const groups = Array.from(groupMap.entries()).map(([key, rows]) => {
      const sorted = [...rows].sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
      return { key, submissions: sorted, representative: sorted[0] }
    }).sort((a, b) => new Date(a.representative.created_at) - new Date(b.representative.created_at))
    
    if (groups.length === 0) {
      tasks.value = []
      waitingCount.value = 0
      return
    }
    
    waitingCount.value = groups.length
    
    const userIds = [...new Set(groups.map((g) => g.representative.user_id))]
    const learnerIds = [...new Set(groups.map((g) => g.representative.learner_id).filter(Boolean))]
    let learnersQuery = supabase
      .from('learners')
      .select('id, parent_id, name, name_kana, birth_date')
      .eq('type', 'child')
    if (learnerIds.length > 0) {
      learnersQuery = learnersQuery.or(`id.in.(${learnerIds.join(',')}),parent_id.in.(${userIds.join(',')})`)
    } else {
      learnersQuery = learnersQuery.in('parent_id', userIds)
    }
    const { data: learnersData, error: learnersError } = await learnersQuery
    
    if (learnersError) {
      console.warn('⚠️ 学習者情報取得エラー:', learnersError)
    }
    // 兄弟対応: learner_id -> learner。learner_id が null の従来データ用に parent_id -> 1人目 のフォールバック
    const learnersById = new Map((learnersData || []).map((l) => [l.id, l]))
    const learnersByParent = new Map()
    for (const l of learnersData || []) {
      if (!learnersByParent.has(l.parent_id)) learnersByParent.set(l.parent_id, l)
    }
    const getLearner = (rep) => (rep.learner_id ? learnersById.get(rep.learner_id) : learnersByParent.get(rep.user_id))
    
    const buildGrade = (learner) => {
      if (!learner?.birth_date) return ''
      const g = getGradeFromBirthDate(learner.birth_date)
      return g === 'その他' ? '' : g
    }
    
    // 従来: groups.slice(0, 10) で上位3件表示のための候補を絞っていた。
    // 全件表示時は取得済みgroups（最大100件相当）すべてを候補にする。
    const candidateGroups = SHOW_ALL_SUBMISSIONS ? groups : groups.slice(0, 10)
    const tasksWithLockCheck = await Promise.all(
      candidateGroups.map(async (group) => {
        const rep = group.representative
        const { data: lockData, error: lockError } = await supabase
          .from('feedback_locks')
          .select('id')
          .eq('submission_id', rep.id)
          .eq('is_active', true)
          .gt('expires_at', new Date().toISOString())
          .maybeSingle()
        
        if (lockError && lockError.code !== 'PGRST116') {
          console.warn(`⚠️ ロックチェックエラー (submission: ${rep.id}):`, lockError)
        }
        if (lockData) return null
        
        // 兄弟対応: 同じ子の練習のみ集計（learner_id で絞る）
        let practiceQuery = supabase
          .from('submissions_v3')
          .select('duration, duration_seconds')
          .eq('user_id', rep.user_id)
          .eq('day', rep.day)
          .eq('type', 'practice')
        if (rep.learner_id != null) practiceQuery = practiceQuery.eq('learner_id', rep.learner_id)
        else practiceQuery = practiceQuery.is('learner_id', null)
        const { data: practiceData } = await practiceQuery
        
        const practiceTotal = (practiceData || []).reduce(
          (sum, p) => sum + (Number(p.duration_seconds) || Number(p.duration) || 0),
          0
        )
        const waitingMs = Date.now() - new Date(rep.created_at).getTime()
        const waitingHours = waitingMs / (1000 * 60 * 60)
        const learner = getLearner(rep)
        const grade = buildGrade(learner)
        const assignmentTitle = `Day ${rep.day} の発音練習`
        
        const representative = {
          ...rep,
          student_name: learner?.name || '不明',
          student_name_kana: learner?.name_kana || '',
          grade,
          practice_duration: practiceTotal,
          audio_duration: rep.duration || 0,
          waitingHours,
          assignment_title: assignmentTitle,
          type: 'assignment'
        }
        
        return {
          id: rep.id,
          representative,
          submissions: group.submissions,
          submissionCount: group.submissions.length,
          student_name: representative.student_name,
          student_name_kana: representative.student_name_kana,
          grade,
          day: rep.day,
          assignment_title: assignmentTitle,
          practice_duration: practiceTotal,
          audio_duration: rep.duration || 0,
          waitingHours,
          type: 'assignment'
        }
      })
    )
    
    const availableTasks = tasksWithLockCheck.filter((t) => t !== null)
    // 従来（上位3件のみ自動表示）に戻したい場合は、下の行を availableTasks.slice(0, 3) に戻すこと。
    tasks.value = SHOW_ALL_SUBMISSIONS ? availableTasks : availableTasks.slice(0, 3)

  } catch (error) {
    console.error('❌ タスク読み込みエラー:', error)
    alert('タスクの読み込みに失敗しました')
  } finally {
    isLoading.value = false
  }
}

// 期限切れロックを削除（DB の RPC で行を DELETE。UPDATE だと行が残り UNIQUE で INSERT できない）
const cleanupExpiredLocks = async () => {
  try {
    const { error } = await supabase.rpc('cleanup_expired_feedback_locks')
    if (error) throw error
  } catch (error) {
    console.error('期限切れロッククリーンアップエラー:', error)
  }
}

// モーダルを閉じるときにその提出のロックを解除（未完了で閉じた場合、他講師・再受付できるようにする）
const releaseFeedbackLockOnClose = async (submissionId) => {
  if (!submissionId) {
    console.warn('⚠️ releaseFeedbackLockOnClose: submissionId が空です')
    return
  }
  try {
    await releaseFeedbackLock(submissionId, supabase)
    console.log('🔓 モーダル閉じに伴いロック解除成功:', submissionId)
  } catch (e) {
    console.error('❌ ロック解除エラー:', e)
    // エラーでもモーダルは閉じるが、ロックが残る可能性があることをユーザーに通知
    alert('ロックの解除に失敗しました。この課題は一時的に受付できない可能性があります。')
  }
}

// タスクを受け入れる（ロックをかける）
const handleAcceptTask = async (task) => {
  if (isProcessing.value) return
  
  isProcessing.value = true
  conflictMessage.value = ''
  
  try {
    // クリック直前に期限切れロックを削除（残っていると 409 の原因になる）
    await cleanupExpiredLocks()
    const result = await acceptTaskWithLock(task, supabase, {
      onSuccess: (lockResult) => openFullScreenFeedback(task, lockResult),
      onConflict: () => {
        conflictMessage.value = '🙏 タッチの差で他のコーチが担当しました！ リストを更新します'
        setTimeout(() => {
          conflictMessage.value = ''
          loadTasks()
        }, 3000)
      }
    })
    if (result?.success) {
      console.log('✅ ロック取得成功')
    }
  } catch (error) {
    console.error('❌ タスク受付エラー:', error)
    alert(`タスクの受付に失敗しました: ${error.message}`)
  } finally {
    isProcessing.value = false
  }
}

// ロック残り時間タイマーを開始
const startLockTimer = () => {
  if (lockTimerInterval.value) {
    clearInterval(lockTimerInterval.value)
    lockTimerInterval.value = null
  }
  const tick = () => {
    if (!lockExpiresAt.value) return
    const now = Date.now()
    const sec = Math.max(0, Math.floor((lockExpiresAt.value - now) / 1000))
    remainingSeconds.value = sec
    remainingTimeDisplay.value = formatRemainingTime(sec)
    if (sec <= 0) {
      if (lockTimerInterval.value) {
        clearInterval(lockTimerInterval.value)
        lockTimerInterval.value = null
      }
      // 画面を開いたまま0:00になった場合も、バックグラウンド復帰時と同様にモーダルを閉じる
      if (showFullScreenFeedback.value) {
        console.log('⏱️ ロック期限切れのためモーダルを閉じます')
        alert('ロックの期限が切れたため、モーダルを閉じます。')
        closeFullScreenFeedback().catch((e) => console.error('closeFullScreenFeedback error', e))
      }
    }
  }
  tick()
  lockTimerInterval.value = setInterval(tick, 1000)
}

// ロック残り時間タイマーを停止
const stopLockTimer = () => {
  if (lockTimerInterval.value) {
    clearInterval(lockTimerInterval.value)
    lockTimerInterval.value = null
  }
  lockExpiresAt.value = null
  remainingTimeDisplay.value = '--:--'
  remainingSeconds.value = 0
}

// 全画面フィードバック開始（task = { representative, submissions } または 従来の1件）
const openFullScreenFeedback = async (task, lockResult) => {
  // 前回モーダルの完了表示を持ち越さない
  resetFeedbackState()
  const representative = task.representative ?? task
  const submissions = task.submissions ?? [task]
  currentFullScreenSubmission.value = representative
  currentFullScreenSubmissions.value = submissions
  if (lockResult?.expires_at) {
    lockExpiresAt.value = new Date(lockResult.expires_at).getTime()
  } else {
    lockExpiresAt.value = Date.now() + 15 * 60 * 1000
  }
  try {
    for (const sub of submissions) {
      try {
        await prefetchPlaybackUrl(sub.id)
      } catch (e) {
        console.warn('prefetch failed for', sub.id, e)
      }
    }
  } finally {
    showFullScreenFeedback.value = true
    startLockTimer()
    await nextTick()
  }
}

// 全画面フィードバック終了（closedAfterSend: 送信成功後に閉じる場合は true＝R2削除しない）
const closeFullScreenFeedback = async (closedAfterSend = false) => {
  // 操作中・動作中は閉じない
  if (!canCloseModal.value) {
    console.warn('録音中またはアップロード中のため、モーダルを閉じることができません')
    return
  }

  const ids = [
    currentFullScreenSubmission.value?.id,
    ...(currentFullScreenSubmissions.value || []).map((s) => s.id)
  ].filter(Boolean)
  const recordingId = currentRecording?.value?.recordingId
  ids.forEach((id) => stopPlayback(id))

  // 録音状態もリセット（await で安全に停止）
  if (isRecording.value) {
    await stopFeedbackRecording()
  }

  // 未送信で閉じる場合: R2のチャンク・結合ファイルを削除し、IndexedDBも破棄
  if (recordingId) {
    if (!closedAfterSend) {
      try {
        await deleteFeedbackRecording(recordingId)
        console.log('🗑️ 未送信のためR2フィードバックデータ削除完了:', recordingId)
      } catch (e) {
        console.warn('⚠️ R2フィードバック削除エラー（IndexedDBは破棄）:', e)
      }
    }
    await cleanupRecordingData(recordingId)
  }

  // ロック残り時間タイマーを停止
  stopLockTimer()

  await releaseFeedbackLockOnClose(currentFullScreenSubmission.value?.id)

  showFullScreenFeedback.value = false
  currentFullScreenSubmission.value = null
  currentFullScreenSubmissions.value = []
  resetFeedbackState()

  // タスクリストを更新
  if (!isProcessing.value) {
    await loadTasks()
  }
}

// 録音開始ボタンの表示・有効化ガード条件
const canShowStartButton = computed(() => {
  return !isRecording.value && !isUploading.value && !isCompleted.value && !finalAudioUrl.value
})

const canStartNow = computed(() => canShowStartButton.value) // 将来の条件追加にも対応

// モーダル閉じ禁止条件（操作中・動作中は閉じられない）
const canCloseModal = computed(() => {
  return !isRecording.value && !isUploading.value && !isDeleting.value && !isSubmitting.value
})

// ロック残り時間の表示用クラス（残り3分で黄色、1分で赤）
const lockTimerClass = computed(() => {
  const sec = remainingSeconds.value
  if (sec <= 60) return 'timer-danger'
  if (sec <= 180) return 'timer-warning'
  return 'timer-normal'
})

// まもなく期限切れの警告表示（残り1分以内）
const showExpiryWarning = computed(() => {
  const sec = remainingSeconds.value
  return sec > 0 && sec <= 60
})

// フィードバック録音モード（音声/動画）。動画はAndroid/PCのみ対応
const isVideoSupported = isVideoFeedbackSupported()
const selectedMediaKind = ref(isVideoSupported ? 'video' : 'audio')

// 録音開始
const startRecording = async () => {
  // 二重起動ガード
  if (!canStartNow.value) {
    console.warn('録音開始条件が満たされていません')
    return
  }
  
  try {
    const submissionData = currentFullScreenSubmission.value
    if (!submissionData) {
      console.error('提出データが見つかりません')
      return
    }
    
    await startFeedbackRecording(submissionData, selectedMediaKind.value)

  } catch (error) {
    console.error('フィードバック録音開始エラー:', error)
    alert(`録音開始エラー: ${error.message}`)
  }
}

// 録音停止（新しいチャンクシステム）
const stopRecording = async () => {
  try {
    await stopFeedbackRecording()
  } catch (error) {
    console.error('フィードバック録音停止エラー:', error)
    alert(`録音停止エラー: ${error.message}`)
  }
}

// フィードバック送信処理
const handleSendFeedback = async () => {
  try {
    console.log('📤 フィードバック送信処理開始')
    
    // sendToLine を実行
    const result = await sendToLine()
    
    console.log('✅ フィードバック送信完了:', result)
    
    // 成功メッセージ
    alert('フィードバックを送信しました！\n生徒のトップからフィードバックを確認できます。')

    // モーダルを閉じる（送信済みのためR2は削除しない）
    await closeFullScreenFeedback(true)
    // 送信成功後は確実に一覧を再取得（モーダル内の loadTasks に依存しない）
    await loadTasks()
    
  } catch (error) {
    console.error('❌ フィードバック送信処理エラー:', error)
    alert(`送信エラー: ${error.message}`)
  }
}

// リストを最新に更新
const refreshTaskList = async () => {
  await loadTasks()
}

// 定期的にリストを更新するためのインターバル
let refreshInterval = null

// イベントリスナー用のハンドラー保存用
let onKeydownHandler = null
let onBeforeUnloadHandler = null

// バックグラウンド復帰時の二重実行防止（同一の復帰で1回だけ閉じる）
let lastVisibilityHandledAt = 0
const VISIBILITY_DEBOUNCE_MS = 2000

// visibilitychange は同一参照で登録・解除する（二重登録を防ぐ）
const handleVisibilityChange = async () => {
  if (document.hidden) {
    if (showFullScreenFeedback.value) {
      console.log('⚠️ モーダル表示中にバックグラウンドに移行しました')
    }
  } else {
    // フォアグラウンドに復帰
    if (showFullScreenFeedback.value && lockExpiresAt.value != null) {
      const now = Date.now()
      if (now >= lockExpiresAt.value) {
        // 同一の復帰イベントで二重実行されないようガード
        if (now - lastVisibilityHandledAt < VISIBILITY_DEBOUNCE_MS) {
          return
        }
        lastVisibilityHandledAt = now
        console.log('🔙 フォアグラウンドに復帰: ロック期限切れのためモーダルを閉じます')
        alert('ロックの期限が切れたため、モーダルを閉じます。')
        await closeFullScreenFeedback()
      } else {
        const remaining = Math.floor((lockExpiresAt.value - now) / 1000)
        console.log(`🔙 フォアグラウンドに復帰: ロックはまだ有効です（残り${remaining}秒）`)
      }
    }
  }
}

// 初期化
onMounted(async () => {
  await loadTasks()

  // 定期的にリストを更新（30秒ごと）
  refreshInterval = setInterval(() => {
    if (!isProcessing.value) {
      loadTasks()
    }
  }, 30000)

  // ESCキーでのモーダル閉じをガード
  onKeydownHandler = (e) => {
    if (e.key === 'Escape') {
      if (!canCloseModal.value) {
        e.preventDefault()
        e.stopPropagation()
        console.warn('操作中のため、ESCキーでモーダルを閉じることができません')
      } else {
        if (showFullScreenFeedback.value) closeFullScreenFeedback()
      }
    }
  }
  window.addEventListener('keydown', onKeydownHandler)

  // ブラウザのリロード/タブ閉じをガード
  onBeforeUnloadHandler = (e) => {
    if (!canCloseModal.value && showFullScreenFeedback.value) {
      e.preventDefault()
      e.returnValue = ''
      console.warn('操作中のため、ページを離れることができません')
    }
  }
  window.addEventListener('beforeunload', onBeforeUnloadHandler)

  // バックグラウンド復帰時: 同一参照で登録（二重登録を防ぐため remove → add）
  document.removeEventListener('visibilitychange', handleVisibilityChange)
  document.addEventListener('visibilitychange', handleVisibilityChange)
})

onUnmounted(() => {
  if (refreshInterval) {
    clearInterval(refreshInterval)
    refreshInterval = null
  }
  
  // ロック残り時間タイマーを停止
  if (lockTimerInterval.value) {
    clearInterval(lockTimerInterval.value)
    lockTimerInterval.value = null
  }
  
  // イベントリスナーを削除
  if (onKeydownHandler) {
    window.removeEventListener('keydown', onKeydownHandler)
    onKeydownHandler = null
  }
  if (onBeforeUnloadHandler) {
    window.removeEventListener('beforeunload', onBeforeUnloadHandler)
    onBeforeUnloadHandler = null
  }
  document.removeEventListener('visibilitychange', handleVisibilityChange)

  cleanupAllPlayers()
  cleanupRecorder()
})

// ルート離脱時のクリーンアップ（保険）
onBeforeRouteLeave(() => {
  if (!canCloseModal.value) {
    // 進行中は遷移を止める
    console.warn('操作中のため、ページ遷移ができません')
    return false
  }
  // 進行中でなければクリーンアップ
  cleanupAllPlayers()
  cleanupRecorder()
})
</script>

<style scoped>
.instructor-home {
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  padding: 1rem;
}

/* ヘッダーエリア */
.motivation-header {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.motivation-stats {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.stat-icon {
  font-size: 1.5rem;
}

.stat-label {
  font-size: 0.9rem;
  color: #6b7280;
  font-weight: 500;
}

.stat-value {
  font-size: 1.2rem;
  font-weight: 700;
  color: #1f2937;
}

/* インフォメーションエリア */
.info-section {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.info-message {
  text-align: center;
  font-size: 1.1rem;
  color: #374151;
  line-height: 1.6;
}

.info-message strong {
  color: #ef4444;
  font-size: 1.3rem;
}

.info-message.success {
  color: #059669;
  font-size: 1.2rem;
}

.day0-link-card {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: #fff8f2;
  border: 1px solid #f0b27a;
  border-radius: 10px;
  padding: 0.9rem 1rem;
  margin-bottom: 1.5rem;
  text-decoration: none;
  color: inherit;
}

.day0-link-icon {
  font-size: 1.4rem;
}

.day0-link-text {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.day0-link-title {
  font-weight: 700;
  color: #1e293b;
  font-size: 0.95rem;
}

.day0-link-sub {
  font-size: 0.8rem;
  color: #6b7280;
}

.day0-link-arrow {
  font-size: 1.3rem;
  color: #d35400;
}

/* タスクカードエリア */
.tasks-section {
  margin-bottom: 1.5rem;
}

.loading, .no-tasks {
  text-align: center;
  padding: 3rem 1rem;
  color: #6b7280;
  background: white;
  border-radius: 12px;
}

.task-cards {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.task-card {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-left: 4px solid #10b981;
}

.task-card.waiting-long {
  border-left-color: #ef4444;
  background: #fef2f2;
}

/* 生徒情報 */
.student-info {
  margin-bottom: 1.5rem;
}

.student-name {
  font-size: 1.3rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 0.5rem;
}

.student-grade {
  font-size: 1rem;
  font-weight: 500;
  color: #6b7280;
}

.assignment-info {
  margin-bottom: 1rem;
  color: #374151;
}

.day-label {
  font-weight: 600;
  color: #3b82f6;
}

.assignment-title {
  color: #6b7280;
  font-size: 0.9rem;
}

.submission-count-badge {
  margin-left: 0.5rem;
  padding: 0.15rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: #047857;
  background: #d1fae5;
  border-radius: 9999px;
}

.metrics {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.metric-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.metric-icon {
  font-size: 1rem;
}

.metric-label {
  font-size: 0.9rem;
  color: #6b7280;
  min-width: 50px;
}

.metric-value {
  font-weight: 600;
  color: #1f2937;
}

.metric-value.waiting-long {
  color: #ef4444;
  font-weight: 700;
}

/* 発注条件ブロック */
.order-terms {
  border-top: 1px solid #e5e7eb;
  padding-top: 1rem;
  margin-bottom: 1.5rem;
  color: #6b7280;
  /* font-sizeは .terms-row-fixed で制御 */
}

.terms-row {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.25rem;
  flex-wrap: wrap;
}

.terms-row-fixed {
  display: flex;
  flex-wrap: nowrap;
  line-height: 1.8;
  width: 100%;
  min-width: 0;
  font-size: clamp(0.7rem, 2.5vw, 0.85rem); /* 画面幅に応じてフォントサイズを調整 */
}

.term-item {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  flex: 0 1 auto;
  min-width: 0;
  white-space: nowrap;
}

.term-item:first-child {
  margin-right: 1rem; /* 2つの項目の間にスペース */
}

.term-label {
  font-weight: 600;
  white-space: nowrap;
  flex-shrink: 0; /* ラベルは縮小しない */
}

.term-value {
  white-space: nowrap;
}

.terms-agreement {
  margin-top: 0.5rem;
  font-size: 0.8rem;
  font-style: italic;
}

/* アクションボタン */
.task-actions {
  text-align: center;
}

.btn-accept {
  width: 100%;
  background: #ef4444;
  color: white;
  padding: 1rem 2rem;
  font-size: 1.1rem;
  font-weight: 700;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-accept:hover:not(:disabled) {
  background: #dc2626;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
}

.btn-accept:disabled {
  background: #d1d5db;
  color: #9ca3af;
  cursor: not-allowed;
}

/* リスト操作エリア */
.list-actions {
  text-align: center;
  margin-bottom: 1.5rem;
}

.btn-refresh {
  background: #6b7280;
  color: white;
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  font-weight: 600;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-refresh:hover:not(:disabled) {
  background: #4b5563;
  transform: translateY(-1px);
}

.btn-refresh:disabled {
  background: #d1d5db;
  color: #9ca3af;
  cursor: not-allowed;
}

/* 競合メッセージ */
.conflict-message {
  background: #fef3c7;
  border: 1px solid #f59e0b;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
  text-align: center;
  color: #92400e;
  font-weight: 600;
}

/* スマホ対応 */
@media (max-width: 768px) {
  .instructor-home {
    padding: 0.5rem;
  }
  
  .motivation-stats {
    gap: 0.75rem;
  }
  
  .stat-item {
    flex-wrap: nowrap;
  }
  
  .task-card {
    padding: 1rem;
  }
  
  .student-name {
    font-size: 1.1rem;
  }
  
  .btn-accept {
    padding: 0.875rem 1.5rem;
    font-size: 1rem;
  }
}

/* ========================================
   全画面フィードバックモーダル（InstructorDashboard.vueからコピー）
   ======================================== */

/* 全画面フィードバックモーダル */
.fullscreen-feedback-modal {
  position: fixed;
  inset: 0;
  background: white;
  z-index: 2000;
  overflow: hidden;
  /* 端末がダークモードでも :root の白文字を継承しない（白背景で文字が消えるのを防ぐ） */
  color-scheme: light;
  color: #1f2937;
}

/* 全画面モーダルアニメーション */
.fullscreen-modal-enter-active {
  transition: all 0.3s ease-out;
}

.fullscreen-modal-leave-active {
  transition: all 0.2s ease-in;
}

.fullscreen-modal-enter-from {
  transform: scale(0.8);
  opacity: 0;
}

.fullscreen-modal-leave-to {
  transform: scale(0.9);
  opacity: 0;
}

.fullscreen-modal-enter-to,
.fullscreen-modal-leave-from {
  transform: scale(1);
  opacity: 1;
}

.fullscreen-content {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  background: #f8fafc;
  border: 0;
  border-radius: 0;
}

.close-btn {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: #ef4444;
  color: white;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  font-size: 1.2rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000;
}

.close-btn:hover {
  background: #dc2626;
}

.close-btn:disabled {
  background: #9ca3af;
  cursor: not-allowed;
  opacity: 0.6;
}

.close-btn:disabled:hover {
  background: #9ca3af;
}

/* ロック残り時間バー（提案1） */
.lock-timer-bar {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  margin: 0 1rem;
  border-radius: 8px;
  font-weight: 600;
  flex-shrink: 0;
}
.lock-timer-bar.timer-normal {
  background: #dcfce7;
  color: #166534;
}
.lock-timer-bar.timer-warning {
  background: #fef3c7;
  color: #92400e;
}
.lock-timer-bar.timer-danger {
  background: #fee2e2;
  color: #991b1b;
}
.lock-timer-label {
  font-size: 0.875rem;
}
.lock-timer-value {
  font-size: 1.125rem;
  font-variant-numeric: tabular-nums;
}
.lock-timer-warning {
  font-size: 0.8125rem;
  margin-left: auto;
}

.feedback-screen {
  flex: 1 1 auto;
  overflow-y: auto;
  padding-bottom: 0 !important;
  max-width: 1200px;
  margin: 0 auto;
}

.feedback-screen h2 {
  color: #1f2937;
  margin-bottom: 1rem;
  text-align: center;
}

/* 左上3段の情報表示：右上ボタンと重ならないよう右余白を確保 */
.feedback-header-mobile {
  padding: 0.5rem 72px 1rem 0.75rem;
  margin-bottom: 1.5rem;
  border-bottom: 2px solid #f3f4f6;
}

.student-name-line {
  font-size: 1.3rem;
  font-weight: bold;
  color: #1f2937;
  line-height: 1.2;
  margin-bottom: 0.25rem;
  text-align: left;
}

.student-kana-line {
  font-size: 1rem;
  color: #6b7280;
  line-height: 1.2;
  margin-bottom: 0.25rem;
  text-align: left;
}

.assignment-line {
  font-size: 1.1rem;
  font-weight: bold;
  color: #3b82f6;
  line-height: 1.2;
  text-align: left;
}

/* 画面幅が広いときも違和感なく */
@media (min-width: 769px) {
  .feedback-header-mobile {
    padding-right: 80px;
  }
}

/* 全画面フィードバックセクション */
.feedback-sections {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.feedback-section {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

/* 音声・録音コントロールセクション（固定表示） */
.feedback-controls-section {
  position: sticky;
  bottom: 0;
  background: #fff;
  border-top: 1px solid #e5e7eb;
  padding: 1rem;
  padding-bottom: calc(1rem + env(safe-area-inset-bottom));
  z-index: 2500;
  box-shadow: 0 -2px 8px rgba(0,0,0,0.1);
  margin-bottom: 0;
}

/* 各フィードバックセクションの個別padding設定 */
.feedback-manual-section {
  padding: 1.5rem;
}

.feedback-assignment-section {
  padding: 1.5rem;
}

.feedback-audio-section {
  margin: 0;
  padding: 0;
  border: none;
  box-shadow: none;
}

.feedback-recording-section {
  margin: 0;
  padding: 0;
  border: none;
  box-shadow: none;
}

/* タブレット/PCでは従来の余白を活かす */
@media (min-width: 769px) {
  .fullscreen-content {
    inset: 1rem;
    border: 2px solid #e5e7eb;
    border-radius: 12px;
  }
  .feedback-screen {
    padding-bottom: 0 !important;
  }
}

.feedback-section h3 {
  margin: 0 0 1rem 0;
  color: #1f2937;
  font-size: 1.1rem;
  border-bottom: 2px solid #f3f4f6;
  padding-bottom: 0.5rem;
}

.manual-content ul {
  margin: 0;
  padding-left: 1.5rem;
  color: #374151;
  line-height: 1.6;
}

.manual-content li {
  margin-bottom: 0.5rem;
}

.assignment-content p {
  margin: 0.75rem 0;
  color: #374151;
  line-height: 1.5;
}

.fullscreen-audio-player {
  width: 100%;
  height: 50px;
  margin: 0;
}

/* 音声プレーヤー（ボタントリガーJIT方式・複数提出対応） */
.submitted-audio-title {
  margin: 0 0 0.5rem 0;
  font-size: 0.95rem;
  font-weight: 600;
  color: #374151;
}

.submitted-audio-section {
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.audio-slot {
  margin: 0;
  padding: 0.5rem 0;
  border-bottom: 1px solid #e5e7eb;
}

.audio-slot:last-child {
  border-bottom: none;
}

.audio-slot-label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #6b7280;
  margin-bottom: 0.35rem;
}

.audio-player-section {
  margin: 0;
}

.recording-start {
  text-align: center;
  margin: 0;
  padding: 0;
}

.media-kind-toggle {
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 0.75rem;
  flex-wrap: wrap;
}

.media-kind-toggle label {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  cursor: pointer;
}

.media-kind-toggle label.is-disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.media-kind-note {
  width: 100%;
  font-size: 0.8rem;
  color: #888;
  margin: 0.25rem 0 0;
}

/* 撮影後のチェック。手元の録画は切り抜く前の映像なので、撮影中のプレビュー・サーバーの変換と同じく
   3:4の枠からはみ出る部分を中央で切って見せる（届く動画と同じ範囲になる。データ自体は変えない） */
.feedback-video-player {
  width: 100%;
  max-width: 280px;
  aspect-ratio: 3 / 4;
  object-fit: cover;
  margin: 0 auto;
  display: block;
  border-radius: 8px;
  background: #000;
}

/* 生ストリームをそのまま表示するため、向きに応じたサイズ・回転はJS側(previewVideoStyle)で
   インラインstyleとして指定する。ここでは固定の3:4の枠だけを用意する */
.preview-outer {
  position: relative;
  width: 100%;
  max-width: 280px;
  aspect-ratio: 3 / 4;
  margin: 0 auto 0.75rem;
  overflow: hidden;
  border-radius: 8px;
  background: #000;
}

.recording-preview {
  display: block;
}

.video-preview-section {
  text-align: center;
}

.rotate-btn {
  display: block;
  margin: 0 auto 0.75rem;
}

.preview-actions {
  display: flex;
  gap: 0.5rem;
  justify-content: center;
}

.recording-active {
  text-align: center;
}

.recording-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
  color: #ef4444;
  font-weight: 600;
}

.recording-dot {
  width: 12px;
  height: 12px;
  background: #ef4444;
  border-radius: 50%;
  animation: pulse 1s infinite;
}

@keyframes pulse {
  0% { opacity: 1; }
  50% { opacity: 0.5; }
  100% { opacity: 1; }
}

.recording-time {
  font-size: 2rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 1rem;
}

/* アップロード進捗スタイル */
.upload-progress-section {
  text-align: center;
  padding: 1rem;
  background: #f8fafc;
  border-radius: 8px;
  margin: 1rem 0;
}

.upload-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #374151;
}

.upload-spinner {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.progress-bar {
  width: 100%;
  height: 8px;
  background: #e5e7eb;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 0.5rem;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #3b82f6, #1d4ed8);
  border-radius: 4px;
  transition: width 0.3s ease;
}

.progress-text {
  font-size: 0.875rem;
  color: #6b7280;
}

/* 録音完了状態のスタイル */
.recording-completed {
  text-align: center;
  padding: 1rem;
  background: #f0fdf4;
  border-radius: 8px;
  border: 1px solid #bbf7d0;
  margin: 1rem 0;
}

.completed-message {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
  font-weight: 600;
  color: #166534;
}

.success-icon {
  font-size: 1.2rem;
}

.feedback-audio-player {
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
  border-radius: 8px;
  background: #f8fafc;
}

.recording-actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
}

/* 無効化ボタンのスタイル */
.btn-disabled {
  background-color: #d1d5db !important;
  color: #9ca3af !important;
  cursor: not-allowed !important;
  opacity: 0.6;
}

.btn-disabled:hover {
  background-color: #d1d5db !important;
  transform: none !important;
}

/* ボタンスタイル */
.btn {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-primary {
  background: #667eea;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #5568d3;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.btn-secondary {
  background: #6b7280;
  color: white;
}

.btn-secondary:hover:not(:disabled) {
  background: #4b5563;
  transform: translateY(-1px);
}

.btn-success {
  background: #10b981;
  color: white;
}

.btn-success:hover:not(:disabled) {
  background: #059669;
  transform: translateY(-1px);
}

.btn-danger {
  background: #ef4444;
  color: white;
}

.btn-danger:hover:not(:disabled) {
  background: #dc2626;
  transform: translateY(-1px);
}

.btn.large {
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
}

/* 全画面モーダルの「フィードバックの手順」を左寄せ */
.fullscreen-feedback-modal .feedback-manual-section .manual-title {
  text-align: left !important;
}

.fullscreen-feedback-modal .feedback-manual-section .manual-content,
.fullscreen-feedback-modal .feedback-manual-section .manual-content .manual-steps,
.fullscreen-feedback-modal .feedback-manual-section .manual-content .manual-steps p {
  text-align: left !important;
  color: #374151;
}
</style>

